import { useState, useMemo, useRef, type ReactNode } from 'react';
import type { SortDirection } from '@/types';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  width?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string | number;
  totalItems?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onSort?: (key: string, direction: SortDirection) => void;
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  rowKey,
  totalItems,
  page = 1,
  pageSize = 25,
  onPageChange,
  onSort,
  onSearch,
  searchPlaceholder = 'Search…',
  isLoading = false,
  emptyMessage = 'No records found.',
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [searchQuery, setSearchQuery] = useState('');

  const total = totalItems ?? data.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function handleSort(key: string) {
    const newDir: SortDirection = sortKey === key && sortDir === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDir(newDir);
    onSort?.(key, newDir);
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value);
    // Debounce: only fire onSearch after 400ms of no typing
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      onSearch?.(value);
    }, 400);
  }

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const sortIndicator = useMemo(() => {
    return (key: string) => {
      if (sortKey !== key) return '';
      return sortDir === 'asc' ? ' ▲' : ' ▼';
    };
  }, [sortKey, sortDir]);

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Search bar */}
      {onSearch && (
        <div className="px-4 py-3 border-b border-border-subtle">
          <div className="relative w-72">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="search"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label={searchPlaceholder}
              className="w-full py-1.5 pl-9 pr-3 rounded-md border border-border bg-page text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`text-left py-2.5 px-4 text-[11px] font-medium uppercase tracking-wider text-text-muted select-none whitespace-nowrap ${
                    col.sortable ? 'cursor-pointer hover:text-text-primary transition-colors' : ''
                  }`}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  {col.header}{col.sortable ? sortIndicator(col.key) : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-text-muted">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-5 w-5 border-2 border-border border-t-baker-accent rounded-full animate-spin" />
                    <span className="text-xs">Loading…</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-text-muted">
                  <div className="flex flex-col items-center gap-1">
                    <svg className="h-7 w-7 text-border" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <span className="text-xs">{emptyMessage}</span>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr
                  key={rowKey(row)}
                  className="border-b border-border-subtle hover:bg-page/80 transition-colors"
                >
                  {columns.map((col) => (
                    <td key={col.key} className="py-2.5 px-4 text-text-primary">
                      {col.render ? col.render(row) : String(row[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {onPageChange && totalPages > 1 && (
        <div className="flex justify-between items-center px-4 py-2.5 border-t border-border-subtle">
          <span className="text-xs text-text-muted">
            Page {page} of {totalPages} · {total} items
          </span>
          <div className="flex gap-1.5">
            <button
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="px-3 py-1 rounded-md border border-border bg-surface text-xs font-medium text-text-secondary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-page hover:border-border transition-all cursor-pointer"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="px-3 py-1 rounded-md border border-border bg-surface text-xs font-medium text-text-secondary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-page hover:border-border transition-all cursor-pointer"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
